import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Logout automatico dopo 10 minuti di inattività (nessun click/tasto/scroll).
const INACTIVITY_LIMIT_MS = 10 * 60 * 1000;

// Tutte le richieste axios devono mandare i cookie httpOnly automaticamente.
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // All'avvio dell'app, prova a recuperare l'utente loggato via cookie.
    // Se il cookie è valido /auth/me ritorna l'user; altrimenti 401 → user resta null.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/auth/me`);
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      // Caso 2FA richiesto: ritorno indicatore al chiamante
      if (response.data.requires_2fa) {
        return {
          success: false,
          requires_2fa: true,
          needs_setup: response.data.needs_setup,
          pending_token: response.data.pending_token,
          email: response.data.email,
        };
      }
      // Backend ha settato il cookie httpOnly. Se l'utente ha suggest_2fa, NON impostiamo subito
      // l'utente nel context per evitare il redirect immediato del PublicRoute: lo facciamo dopo
      // la chiusura del prompt 2FA tramite completeLoginWith2FA(userData).
      const { user: userData, suggest_2fa } = response.data;
      if (suggest_2fa) {
        return {
          success: false,
          suggest_2fa: true,
          pending_user: userData,
        };
      }
      setUser(userData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Errore durante il login',
      };
    }
  };

  const completeLoginWith2FA = (_token, userData) => {
    // Il backend ha già settato il cookie. Il primo argomento (token) è ignorato.
    setUser(userData);
  };

  const register = async () => {
    return { success: false, error: 'Registrazione pubblica disabilitata.' };
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (e) {
      console.warn('Logout API error:', e);
    }
    setUser(null);
  };

  // Logout automatico per inattività: attivo solo quando l'utente è loggato.
  useEffect(() => {
    if (!user) return;
    let timer;
    const doAutoLogout = async () => {
      await logout();
      toast.info('Sessione terminata per inattività (10 min). Effettua di nuovo l\'accesso.');
    };
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(doAutoLogout, INACTIVITY_LIMIT_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user]);

  const isAdmin = () => user?.ruolo === 'admin';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      isAdmin,
      fetchUser: async () => {
        try {
          const { data } = await axios.get(`${API_URL}/auth/me`);
          setUser(data);
        } catch {
          setUser(null);
        }
      },
      completeLoginWith2FA,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve essere usato dentro AuthProvider');
  }
  return context;
};
